import React, { useCallback, useEffect, useRef, useState } from 'react';
import Axios from 'axios';
import Text from '@/components/ui/Text';
import Icon from '@/components/ui/Icon';
import { Button } from '@/components/ui/Buttons';
import { GradientSpinner } from '@/components/ui/Spinner';
import setupCsrfToken from '@/modules/SetupCSRFToken';
import cn from '@/utils/cn';
import tciLogo from '../../../../images/tci-logo.svg';

interface PreparingDownloadProps {
  assessmentTitle: string;
  createUrl: string;
}

type Phase = 'preparing' | 'ready' | 'error';

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 60; // ~2 minutes before giving up
const CLOSE_DELAY_MS = 1200; // let the download commit before the tab closes itself

// Trigger the download without navigating the document, so the tab stays open and script-closeable.
const triggerDownload = (url: string) => {
  const link = document.createElement('a');
  link.href = url;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const PreparingDownload = ({ assessmentTitle, createUrl }: PreparingDownloadProps) => {
  const [phase, setPhase] = useState<Phase>('preparing');
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const poll = useCallback((statusUrl: string, attempt: number) => {
    if (attempt > MAX_ATTEMPTS) {
      setPhase('error');
      return;
    }

    Axios.get(statusUrl)
      .then(({ data }) => {
        if (data.status === 'completed') {
          // A completed job with no URL is a failure, not a reason to keep polling.
          if (data.download_url) {
            setPhase('ready');
            triggerDownload(data.download_url);
            // Self-close only a tab the app opened (window.opener); else leave the ready state up.
            if (window.opener) {
              timer.current = setTimeout(() => window.close(), CLOSE_DELAY_MS);
            }
          } else {
            setPhase('error');
          }
        } else if (data.status === 'failed') {
          setPhase('error');
        } else {
          timer.current = setTimeout(() => poll(statusUrl, attempt + 1), POLL_INTERVAL_MS);
        }
      })
      .catch(() => setPhase('error'));
  }, []);

  const start = useCallback(() => {
    setPhase('preparing');

    // The blank_page layout may not run the application pack's CSRF setup, so prime it here.
    setupCsrfToken();
    Axios.post(createUrl)
      .then(({ data }) => poll(data.status_url, 1))
      .catch(() => setPhase('error'));
  }, [createUrl, poll]);

  useEffect(() => {
    start();
    return () => clearTimeout(timer.current);
  }, [start]);

  return (
    <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-px-6">
      <div
        aria-live="polite"
        className={cn(
          'tw-flex tw-w-full tw-max-w-[600px] tw-flex-col tw-items-center tw-text-center',
          'tw-rounded-lg tw-border tw-border-light-grey tw-bg-white tw-px-14 tw-py-12',
          'tw-shadow-[0_6px_24px_0_rgba(41,31,82,0.1)]',
        )}
        role="status"
      >
        <img alt="TCI" className="tw-mb-7 tw-w-[110px]" src={tciLogo} />

        {phase === 'error' ? (
          <>
            <div
              className={cn(
                'tw-mb-6 tw-flex tw-h-[92px] tw-w-[92px] tw-items-center tw-justify-center',
                'tw-rounded-full tw-bg-[#fde7e8]',
              )}
            >
              <Icon className="tw-text-red" icon="Warning" size={40} />
            </div>
            <Text className="tw-mb-3 tw-text-tci-black" tag="h1" variant="heading2">
              Couldn’t create your file. Try again.
            </Text>
            <Text className="tw-mb-6 tw-text-tci-black" size="lg" tag="p" weight="bold">
              {assessmentTitle}
            </Text>
            <Button onClick={start}>
              <span className="tw-flex tw-items-center tw-gap-2">
                <Icon fill="#fff" icon="Refresh" size={18} />
                Try again
              </span>
            </Button>
          </>
        ) : (
          <>
            <div className="tw-mb-6">
              <GradientSpinner size={72} />
            </div>
            <Text className="tw-mb-3 tw-text-tci-black" tag="h1" variant="heading2">
              {phase === 'ready' ? 'Download started' : 'Preparing your download…'}
            </Text>
            <Text className="tw-mb-1.5 tw-text-tci-black" size="lg" tag="p" weight="bold">
              {assessmentTitle}
            </Text>
            <Text className="tw-text-dark-grey">
              {phase === 'ready'
                ? 'You can close this tab.'
                : 'Keep this tab open — it will open automatically.'}
            </Text>
          </>
        )}
      </div>
    </div>
  );
};

export default PreparingDownload;
